import { Alert } from '@mantine/core';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getOrgSubscriptions,
  initiatePayment,
  undoCancelAppSubscription,
  type OrgAppSubscription,
} from '../../api/appPricing';
import { getOrgId } from '../../hooks/useRoute';
import { useLanguage } from '../../i18n/LanguageProvider';
import { AppId } from '../../pricing/data';
import { formatNaturalList } from '../../utils/formatList';
import { useAppSwitcherContext } from '../AppSwitcherProvider';
import { useOrgContext } from '../org/OrgProvider';
import { StyledButton } from '../shared/StyledButton';

type Severity = 'info' | 'warning' | 'error';

const GRACE_PERIOD_DAYS = 3;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const severityToColor: Record<Severity, string> = {
  info: 'blue',
  warning: 'yellow',
  error: 'red',
};

const severityIcon: Record<Severity, React.ReactNode> = {
  info: <IconInfoCircle size={20} />,
  warning: <IconAlertTriangle size={20} />,
  error: <IconAlertCircle size={20} />,
};

interface BannerState {
  message: string;
  severity: Severity;
  actionLabel: string;
  actionType: 'pay' | 'manage' | 'restore';
}

const APP_DISPLAY: Record<string, string> = {
  clientell: 'Clientell',
  whatchat: 'WhatChat',
  rakine: 'Rakine',
  imminote: 'Imminote',
};

export const SubscriptionBanner = () => {
  const { messages, language } = useLanguage();
  const { org } = useOrgContext();
  const { app } = useAppSwitcherContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const orgId = getOrgId(pathname);

  const [subs, setSubs] = useState<OrgAppSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const p = messages.subscriptionBanner ?? ({} as Record<string, string>);

  useEffect(() => {
    if (!org?.id) {
      // Clear stale state when navigating away from an org or
      // switching orgs so the previous org's banner doesn't
      // persist on unrelated pages.
      setSubs([]);
      setLoading(true);
      return;
    }
    setLoading(true);
    getOrgSubscriptions(org.id)
      .then((data: OrgAppSubscription[]) => {
        setSubs(data.filter(s => s.status !== 'canceled'));
      })
      .catch(() => setSubs([]))
      .finally(() => setLoading(false));
  }, [org?.id]);

  const bannerState = useMemo((): BannerState | null => {
    if (loading || subs.length === 0) return null;

    const now = Date.now();
    // Use the shared period_end from any sub (unified billing)
    const periodEnd = subs[0]?.currentPeriodEnd
      ? new Date(subs[0].currentPeriodEnd).getTime()
      : null;

    if (!periodEnd) return null;

    const daysUntilEnd = (periodEnd - now) / MS_PER_DAY;
    const gracePeriodEnd = periodEnd + GRACE_PERIOD_DAYS * MS_PER_DAY;
    const allTrialing = subs.every(s => s.status === 'trialing');
    const cancellingApps = subs.filter(s => s.cancelAtPeriodEnd);
    const hasExpired = subs.some(s => s.status === 'past_due');

    const dateStr = new Date(periodEnd).toLocaleDateString(language);
    const graceStr = new Date(gracePeriodEnd).toLocaleDateString(language);

    // Priority 1: Grace period (period_end passed, within grace)
    if (daysUntilEnd < 0 && now < gracePeriodEnd && !allTrialing) {
      return {
        message: (
          p.gracePeriod ??
          'Payment overdue. Pay by {date} to keep your apps active.'
        ).replace('{date}', graceStr),
        severity: 'warning',
        actionLabel: p.payNow ?? 'Pay now',
        actionType: 'pay',
      };
    }

    // Priority 2: Trial ending soon (≤ 3 days)
    if (allTrialing && daysUntilEnd > 0 && daysUntilEnd <= 3) {
      const days = Math.ceil(daysUntilEnd);
      const dayUnit =
        days === 1
          ? language === 'fr'
            ? 'jour'
            : 'day'
          : language === 'fr'
            ? 'jours'
            : 'days';
      return {
        message: (
          p.trialEndingSoon ?? 'Your trial ends in {days} {dayUnit} ({date}).'
        )
          .replace('{days}', String(days))
          .replace('{dayUnit}', dayUnit)
          .replace('{date}', dateStr),
        severity: 'warning',
        actionLabel: p.managePlan ?? 'Manage Plan',
        actionType: 'manage',
      };
    }

    // Priority 3: Apps cancelling at renewal
    if (cancellingApps.length > 0) {
      const names = cancellingApps.map(s => APP_DISPLAY[s.appId] || s.appId);
      return {
        message: (
          p.appsCancelling ?? '{apps} will be cancelled at renewal ({date}).'
        )
          .replace(
            '{apps}',
            formatNaturalList(names, language === 'fr' ? 'et' : 'and')
          )
          .replace('{date}', dateStr),
        severity: 'warning',
        actionLabel: p.restore ?? 'Restore',
        actionType: 'restore',
      };
    }

    // Priority 4: Renewal coming (3–7 days)
    if (!allTrialing && daysUntilEnd > 0 && daysUntilEnd <= 7) {
      return {
        message: (
          p.renewalComing ?? 'Your subscription renews on {date}.'
        ).replace('{date}', dateStr),
        severity: 'info',
        actionLabel: p.payNow ?? 'Pay now',
        actionType: 'pay',
      };
    }

    // Priority 5: Trial active, > 3 days left
    if (allTrialing && daysUntilEnd > 3) {
      return {
        message: (p.trialActive ?? 'Your trial ends on {date}.').replace(
          '{date}',
          dateStr
        ),
        severity: 'info',
        actionLabel: p.managePlan ?? 'Manage Plan',
        actionType: 'manage',
      };
    }

    // Priority 6: Expired / past_due
    if (allTrialing && daysUntilEnd < 0) {
      return {
        message:
          p.trialExpired ??
          'Your trial has ended. Subscribe to keep using your apps.',
        severity: 'error',
        actionLabel: p.subscribe ?? 'Subscribe',
        actionType: 'manage',
      };
    }

    if (hasExpired || daysUntilEnd < 0) {
      return {
        message:
          p.expired ??
          'Your subscription has expired. Re-subscribe to regain access.',
        severity: 'error',
        actionLabel: p.managePlan ?? 'Manage Plan',
        actionType: 'manage',
      };
    }

    // Priority 7: Healthy — no banner
    return null;
  }, [subs, loading, language, p]);

  const handleAction = useCallback(async () => {
    if (!bannerState || !org?.id || !orgId) return;
    setActionLoading(true);
    try {
      if (bannerState.actionType === 'pay') {
        const origin = window.location.origin;
        const homePath = `/${app}/orgs/${org.id}`;
        const payment = await initiatePayment(
          org.id,
          undefined,
          {
            successRedirectUrl: `${origin}${homePath}?payment=success&at=${Date.now()}`,
            errorRedirectUrl: `${origin}${homePath}?payment=failed&at=${Date.now()}`,
          },
          true // forceRenewal
        );
        if (payment?.link) {
          window.location.href = payment.link;
          return;
        }
      } else if (bannerState.actionType === 'restore') {
        const cancellingApps = subs.filter(s => s.cancelAtPeriodEnd);
        await Promise.all(
          cancellingApps.map(s =>
            undoCancelAppSubscription(org.id, s.appId as AppId)
          )
        );
        // Refresh subs
        const fresh: OrgAppSubscription[] = await getOrgSubscriptions(org.id);
        setSubs(fresh.filter(s => s.status !== 'canceled'));
      } else {
        navigate(`/${app}/orgs/${org.id}/checkout`);
      }
    } catch (err) {
      console.error('Banner action failed:', err);
      // Fallback: navigate to checkout
      navigate(`/${app}/orgs/${org.id}/checkout`);
    } finally {
      setActionLoading(false);
    }
  }, [bannerState, org, orgId, app, subs, navigate]);

  if (pathname.includes('/checkout')) return null;

  if (!bannerState) return null;

  return (
    <div style={{ width: '100%', marginBottom: 16 }}>
      <Alert
        color={severityToColor[bannerState.severity]}
        icon={severityIcon[bannerState.severity]}
        styles={{
          root: { padding: '10px 16px', alignItems: 'center' },
          message: { fontSize: '15px', fontWeight: 500, flex: 1 },
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ flex: 1 }}>{bannerState.message}</span>
          <StyledButton
            variant={bannerState.severity === 'error' ? 'error' : 'success'}
            size="small"
            onClick={handleAction}
            disabled={actionLoading}
            style={{
              padding: '4px 12px',
              fontSize: '14px',
              flexShrink: 0,
            }}
          >
            {actionLoading ? '...' : bannerState.actionLabel}
          </StyledButton>
        </div>
      </Alert>
    </div>
  );
};
